import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VendorPersonType } from '@econtractor/types';
import * as apiVendors from 'api/apiVendors';
import { useToast } from 'context/ToastContext';

export interface VendorPersonFormValues {
    fullName: string;
    position: string;
    description: string;
}

const emptyForm: VendorPersonFormValues = {
    fullName: '',
    position: '',
    description: '',
};

interface UseOffVendorPersonsParams {
    vendorId: number | null;
    onHide: () => void;
    onPrimaryPersonChange?: (person: VendorPersonType | null) => void;
}

const useOffVendorPersons = ({ vendorId, onHide, onPrimaryPersonChange }: UseOffVendorPersonsParams) => {
    const { t } = useTranslation(['Vendors', 'common']);
    const { showToast } = useToast();
    const [list, setList] = useState<VendorPersonType[] | null>(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [isCreate, setIsCreate] = useState(false);
    const [editingPerson, setEditingPerson] = useState<VendorPersonType | null>(null);
    const [contactsPerson, setContactsPerson] = useState<VendorPersonType | null>(null);

    const show = vendorId !== null;
    const isEdit = editingPerson !== null;
    const showForm = isCreate || isEdit;

    const fetchList = useCallback(async () => {
        if (!vendorId) return;
        const res = await apiVendors.getVendorPersons(vendorId);
        setShowSpinner(false);
        if (!res) {
            showToast(t('common:messError'), 'danger');
            setList((prev) => prev ?? []);
            return;
        }
        setList(res);
    }, [vendorId, showToast, t]);

    const fetchListRef = useRef(fetchList);
    fetchListRef.current = fetchList;

    const resetForm = useCallback(() => {
        setIsCreate(false);
        setEditingPerson(null);
        setForm(emptyForm);
    }, []);

    const handleClose = () => {
        resetForm();
        setContactsPerson(null);
        setList(null);
        onHide();
    };

    const handleAdd = () => {
        setEditingPerson(null);
        setForm(emptyForm);
        setIsCreate(true);
    };

    const handleEdit = (row: VendorPersonType) => {
        setIsCreate(false);
        setEditingPerson(row);
        setForm({
            fullName: row.fullName || '',
            position: row.position || '',
            description: row.description || '',
        });
    };

    const changeForm = (patch: Partial<VendorPersonFormValues>) => {
        setForm((prev) => ({ ...prev, ...patch }));
    };

    const hideContacts = () => setContactsPerson(null);

    const removePerson = async (row: VendorPersonType) => {
        if (!vendorId) return;
        setShowSpinner(true);
        const stat = await apiVendors.deleteVendorPerson(vendorId, row.ID);
        await fetchList();
        setShowSpinner(false);
        if (stat) {
            if (editingPerson?.ID === row.ID) resetForm();
            if (contactsPerson?.ID === row.ID) setContactsPerson(null);
            if (row.isPrimary) onPrimaryPersonChange?.(null);
            showToast(t('persons.messDeleted'), 'success');
        } else {
            showToast(t('common:messError'), 'danger');
        }
    };

    const setPrimary = async (row: VendorPersonType, isPrimary: boolean) => {
        if (!vendorId) return;
        setShowSpinner(true);
        const result = await apiVendors.setVendorPersonPrimary(vendorId, row.ID, isPrimary);
        setShowSpinner(false);
        if (!result.ok) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        setList((prev) =>
            (prev || []).map((item) => ({
                ...item,
                isPrimary: isPrimary && item.ID === row.ID,
            })),
        );
        onPrimaryPersonChange?.(isPrimary ? { ...row, isPrimary: true } : null);
        showToast(isPrimary ? t('persons.messPrimarySet') : t('persons.messPrimaryCleared'), 'success');
    };

    const handleSubmit = async () => {
        if (!vendorId) return;
        const fullName = form.fullName.trim();
        if (!fullName) {
            showToast(t('persons.messNameRequired'), 'danger');
            return;
        }

        const payload = {
            fullName,
            position: form.position.trim(),
            description: form.description.trim(),
        };

        setSaving(true);
        const result = editingPerson
            ? await apiVendors.updateVendorPerson(vendorId, editingPerson.ID, payload)
            : await apiVendors.createVendorPerson(vendorId, payload);
        setSaving(false);

        if (!result.ok) {
            showToast(t('common:messError'), 'danger');
            return;
        }

        const wasEdit = Boolean(editingPerson);
        const wasPrimary = Boolean(editingPerson?.isPrimary);
        setShowSpinner(true);
        await fetchList();
        resetForm();
        if (wasEdit && wasPrimary && result.person) {
            onPrimaryPersonChange?.(result.person);
        }
        showToast(wasEdit ? t('persons.messUpdated') : t('persons.messSaved'), 'success');
    };

    useEffect(() => {
        if (!vendorId) return;
        setList(null);
        resetForm();
        setContactsPerson(null);
        void fetchListRef.current();
    }, [vendorId, resetForm]);

    const title = showForm
        ? isEdit
            ? t('persons.titleUpdate')
            : t('persons.titleCreate')
        : t('persons.title');

    return {
        show,
        list,
        showSpinner,
        saving,
        form,
        isEdit,
        showForm,
        contactsPerson,
        title,
        handleClose,
        handleAdd,
        handleEdit,
        handleSubmit,
        resetForm,
        changeForm,
        openContacts: setContactsPerson,
        hideContacts,
        removePerson,
        setPrimary,
    };
};

export default useOffVendorPersons;
